// AuthLayout — unauthenticated shell per design: gray page, centered white
// card (mint aside with concentric rings + device art | form panel), and the
// terms footer below the card. Shared by all auth screens.

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/store/hooks';
import { appBrand } from '@/mocks/user';
import logoBlack from '@/assets/images/midani-logo-black.png';
import logoWhite from '@/assets/images/midani-logo-white.png';
import PhoneMockup from './PhoneMockup';
import BrowserMockup from './BrowserMockup';

interface AuthLayoutProps {
  /** login: phone only · signup: dashboard browser behind the phone. */
  variant: 'login' | 'signup';
  children: ReactNode;
}

const AuthLayout = ({ variant, children }: AuthLayoutProps) => {
  const { t } = useTranslation();
  const mode = useAppSelector((state) => state.theme.mode);

  const comingSoon = () => toast(t('text_coming_soon'));

  return (
    <div className='auth-layout'>
      <div className={`auth-layout__card auth-layout__card--${variant}`}>
        <aside className='auth-layout__aside'>
          <span className='auth-layout__ring auth-layout__ring--1' aria-hidden='true' />
          <span className='auth-layout__ring auth-layout__ring--2' aria-hidden='true' />
          <span className='auth-layout__ring auth-layout__ring--3' aria-hidden='true' />
          <img
            className='auth-layout__logo'
            src={mode === 'dark' ? logoWhite : logoBlack}
            alt={appBrand.name}
          />
          <div className={`auth-layout__art auth-layout__art--${variant}`}>
            {variant === 'signup' && <BrowserMockup className='auth-layout__browser' />}
            <PhoneMockup className='auth-layout__phone' />
          </div>
        </aside>
        <main className='auth-layout__main'>{children}</main>
      </div>

      <p className='auth-layout__terms'>
        {t('text_terms_footer_intro')}{' '}
        <button type='button' className='auth-layout__terms-link' onClick={comingSoon}>
          {t('text_terms_of_use')}
        </button>{' '}
        {t('text_and')}{' '}
        <button type='button' className='auth-layout__terms-link' onClick={comingSoon}>
          {t('text_privacy_notice')}
        </button>
        {'. '}
        {t('text_terms_footer_rest')}
      </p>
    </div>
  );
};

export default AuthLayout;
